import type { TenantHostContext } from "./tenant-host";

/**
 * QL-628 — the advisor web app's route classes, shared so they can be unit-tested
 * from apps/api (apps/web has no test runner; same reason as standalone-flow.ts).
 * Keep in sync with apps/web/src/router.tsx.
 */

/** Pages that render without the app shell or stale-session checks (AppShell). */
const PUBLIC_WEB_PATHS = [
  "/login",
  "/clear",
  "/forgot-password",
  "/reset-password",
  "/tos",
  "/privacy",
];

/** Exact AppShell semantics (moved from app-shell.tsx): `/login/*` sub-pages are NOT public here. */
export function isPublicWebPath(pathname: string): boolean {
  return (
    PUBLIC_WEB_PATHS.includes(pathname) ||
    pathname.startsWith("/p/") ||
    pathname.startsWith("/s/") ||
    pathname.startsWith("/oauth/")
  );
}

/**
 * Routes rendered under AuthGuard (router.tsx "Protected routes"): everything that
 * is neither public nor a login sub-page (/login/verify-2fa, /login/sso-error,
 * /login/sso-link — those render without a session and must never trigger the
 * app bootstrap).
 */
export function isProtectedWebPath(pathname: string): boolean {
  return !isPublicWebPath(pathname) && !pathname.startsWith("/login/");
}

/**
 * QL-628 — whether to request /internal/app-bootstrap before get-session has
 * answered. True only on a cold load of a protected route off the root domain:
 * there the app needs the bootstrap as soon as a session exists, and a 401
 * (no session) is harmless. The root domain always ends in a full-page redirect
 * to the tenant host, so a request there would be thrown away.
 */
export function shouldPrefetchAppBootstrap(input: {
  sessionPending: boolean;
  hostKind: TenantHostContext["kind"];
  pathname: string;
}): boolean {
  return input.sessionPending && input.hostKind !== "root" && isProtectedWebPath(input.pathname);
}
